import { Link } from 'react-router-dom';
import { ProductImage } from '../../ui/ProductImage';
import { CartGifts } from '../cart/CartGifts';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { groupCart } from '../../core/rules/cartBundles';
import { shopLabels } from '../../data/shopLabels';
import { isSoldOut } from '../../core/rules/stock';
import { euro } from '../../core/format/currency';
import { euroCents } from './checkoutRules';
import { VoucherField } from './VoucherField';
import type { CheckoutControl } from './useCheckout';

/** De samenvatting naast het formulier: regels, cadeau, verzending en totaal. */
export function OrderSummary({
  c
}: {
  c: CheckoutControl;
}) {
  const {
    lines,
    subtotal,
    giftSubtotal,
    shipping,
    total,
    gifts,
    nextGift,
    appliedCode
  } = c.cart;
  const {
    remove
  } = useCartActions();
  const {
    openCart
  } = useOverlayActions();
  const {
    routineById
  } = useCatalog();
  const {
    voucher
  } = c;
  return <aside className="lg:col-span-5" aria-labelledby="co-summary" data-id="element-7336" data-mp="suwbvlpou2s7">
      <div id="co-summary-box" className="scroll-mt-24 bg-shell p-6 lg:sticky lg:top-28 lg:p-8" data-id="element-7337" data-mp="ot2d5iz1z8uo">
        <h2 id="co-summary" className="scroll-mt-24 font-display text-xl text-ink" data-id="element-7338" data-mp="jty5iwrs1j6g">
          Je bestelling
        </h2>
        <ul className="mt-5 divide-y divide-line border-y border-line" data-id="element-7339" data-mp="evei0rxjbzbz">
          {groupCart(lines).flatMap(g => {
          if (g.kind === 'line') return [g.line];
          // Een protocol als bundel: één kopje erboven, zelfde regels eronder (compact).
          return [{
            heading: g.routineId,
            count: g.lines.length
          } as const, ...g.lines];
        }).map(l => {
          if ('heading' in l) {
            return <li key={`bundle-${l.heading}`} className="pb-1 pt-4 text-[13px] text-clay" data-id="element-7340" data-mp="xt7kjt48y78u">
                  {routineById(l.heading)?.name ?? 'Protocol'} · {l.count} producten
                </li>;
          }
          const problem = c.lineProblem(l.product.id, isSoldOut(l.product));
          return <li key={l.product.id} className={`flex items-center gap-4 py-4 ${l.bundle ? 'pl-3' : ''}`} data-id="element-7341" data-mp="3ksme7to13ivk">
                <div className="relative" data-id="element-7342" data-mp="3b0go6phc7bj0">
                  {/* Decoratief: de naam staat ernaast. */}
                  <ProductImage src={l.product.image} alt="" className="h-16 w-14 shrink-0 p-1.5" data-id="element-7343" data-mp="3fzju0c555ls1" />
                  <span aria-hidden="true" className="absolute -right-2 -top-2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-ink px-1 text-[11px] text-shell" data-id="element-7344" data-mp="3129aqgm9bblr">
                    {l.qty}
                  </span>
                </div>
                <div className="flex-1" data-id="element-7345" data-mp="2x05uzfsk1kuo">
                  <p className="text-[15px] leading-snug text-ink" data-id="element-7346" data-mp="2x17i4imuhxsr">
                    <span className="sr-only" data-id="element-7347" data-mp="q9yd45fawimf">{shopLabels.lineQty(l.qty)} </span>
                    {l.product.name}
                  </p>
                  <p className="text-[13px] text-muted" data-id="element-7348" data-mp="v8jbdzazss88">{l.subscribe ? c.refill.tooSmall ? 'Bijvullen: doos te klein, zie hieronder' : `In je bijvuldoos · elke ${c.refill.weeks} weken als het bijna op is` : l.product.brand}</p>
                  {problem && <p className="text-[13px] text-clay" data-id="element-7349" data-mp="1e6l76fcdhwn1">
                      {problem} ·{' '}
                      <button type="button" onClick={() => remove(l.product.id)} className="text-ink underline underline-offset-4" data-id="element-7350" data-mp="19el3cbt6mjl6">
                        Weghalen<span className="sr-only" data-id="element-7351" data-mp="1o5tx8x44ar44">: {l.product.name}</span>
                      </button>
                    </p>}
                </div>
                <span className="text-[15px] text-ink" data-id="element-7352" data-mp="1x348wjkuki9k">{euroCents(l.product.price * l.qty)}</span>
              </li>;
        })}
        </ul>

        {/* Slim bijvullen: een bijvuldoos haalt altijd het minimum. Anders eerst in de mand aanpassen (de server weigert het ook). */}
        {c.refill.tooSmall && <p role="alert" className="mt-4 bg-sand p-3 text-[13px] leading-relaxed text-ink" data-id="element-7353" data-mp="3uw020bsg8rhv">
            Met alleen dit wordt een bijvuldoos kleiner dan {euro(c.policyMinBox)}. Zet nog iets op bijvullen of kies eenmalig in je{' '}
            <button type="button" onClick={openCart} className="underline underline-offset-4" data-id="element-7354" data-mp="96mwqumqqzpn">
              winkelmand
            </button>
            .
          </p>}

        {/* Dezelfde cadeauregels als in de mand: hier kan ze het reisformaat nog wijzigen. */}
        <CartGifts gifts={gifts} compact data-id="element-7355" data-mp="3dirvjxyq7z7q" />
        {nextGift && <p className="mt-3 text-[13px] text-muted" data-id="element-7356" data-mp="3hn9e5ttco6gx">
            Nog {euro(nextGift.threshold - giftSubtotal)} tot {nextGift.phrase}.{' '}
            <Link to="/webshop" className="text-ink underline underline-offset-4" data-id="element-7357" data-mp="3mjq1ilsq6gsy">
              Aanvullen
            </Link>
          </p>}

        <dl className="mt-6 space-y-2 text-[14px] text-muted" data-id="element-7358" data-mp="2tvu8br2d770x">
          <div className="flex justify-between" data-id="element-7359" data-mp="2yvymhafsvpnz">
            <dt data-id="element-7360" data-mp="2yunjhcu6ilxx">Subtotaal</dt>
            <dd className="text-ink" data-id="element-7361" data-mp="2ytlxwefjeh1y">{euroCents(subtotal)}</dd>
          </div>
          <div className="flex justify-between" data-id="element-7362" data-mp="2rbxlsikqcfeg">
            <dt data-id="element-7363" data-mp="2mcuoxlewlfhk">Verzending</dt>
            <dd className={shipping === 0 ? 'text-clay' : 'text-ink'} data-id="element-7364" data-mp="2menyb8gwidt9">{shipping === 0 ? 'Gratis' : euroCents(shipping)}</dd>
          </div>
          {appliedCode && <div className="flex justify-between" data-id="element-7365" data-mp="2dd7l6sypbxlc">
              <dt data-id="element-7366" data-mp="2dceoylxdcqhg">Code</dt>
              <dd className="text-ink" data-id="element-7367" data-mp="28dl7kieyofc0">{appliedCode.code}</dd>
            </div>}
          {voucher.cents > 0 && <>
              <div className="flex justify-between" data-id="element-7368" data-mp="23ltqcyhp3lyj">
                <dt data-id="element-7369" data-mp="1ynrybo2akcax">Totaal</dt>
                <dd className="text-ink" data-id="element-7370" data-mp="1yn0etblyy510">{euroCents(total)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-7371" data-mp="1toyruflf9lk9">
                <dt data-id="element-7372" data-mp="1tqiceie42iru">Cadeaubon ···{voucher.last4}</dt>
                <dd className="text-clay" data-id="element-7373" data-mp="1pnlms7q5lmee">−{euroCents(voucher.cents / 100)}</dd>
              </div>
            </>}
        </dl>
        <div className="mt-4 flex items-baseline justify-between border-t border-line pt-4" data-id="element-7374" data-mp="1kois9h7suyko">
          <span className="text-ink" data-id="element-7375" data-mp="1azlji62gi1gw">
            {voucher.cents > 0 ? 'Te betalen' : 'Totaal'} <span className="text-[13px] text-muted" data-id="element-7376" data-mp="1f31fr1am5zt2">incl. btw</span>
          </span>
          <span className="font-display text-3xl text-ink" data-id="element-7377" data-mp="1130gtrdwt2g8">{euroCents(voucher.payableCents / 100)}</span>
        </div>
        {voucher.cents > 0 && voucher.payableCents === 0 && <p className="mt-2 text-[13px] text-muted" data-id="element-7378" data-mp="1tr4qenzlpzo7">Je cadeaubon dekt alles. Je hoeft niets meer te betalen.</p>}
        <VoucherField v={voucher} data-id="element-7379" data-mp="1yq8y1a6vnpcf" />
      </div>
    </aside>;
}